import React, { useEffect, useState } from 'react';
import {
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Chip,
    Select,
    MenuItem,
    InputLabel,
    OutlinedInput,
    Box,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import { getAccounts } from '@widgets/AccountsTable/api';
import { useAsyncData } from '@shared/react';
import { addUsers } from '@widgets/UserManagementTable/api';
import useStore from '@widgets/UserManagementTable/lib/store/drawer-store';
import ClientRefreshStore from '@widgets/UserManagementTable/lib/store/clients-refresh-store';
import snackbarStore from '@widgets/MainLayout/store/snackbar-store';
import './CreateUser.scss';

interface CreateUserFormProps {
    onCancel: () => void;
    selectedAccount: any;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onCancel, selectedAccount }) => {
    const [role, setRole] = useState(selectedAccount ? 'Sub Account' : 'Advisor');
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [assignedAccounts, setAssignedAccounts] = useState<any[]>([]); // Store full account objects

    const [assignedAccount, setAssignedAccount] = useState<any>(selectedAccount);
    const [privileges, setPrivileges] = useState({
        canLogin: false,
        canSeePosition: false,
        canSee1099: false,
        setPreferences: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>('');
    const setIsDrawerOpen = useStore((state) => state.setIsDrawerOpen);

    const setClientRefresh = ClientRefreshStore((state) => state.setClientRefresh);
    const { setSnackbarMessage } = snackbarStore();

    useEffect(() => {
        if (isFormComplete()) setErrorMessage('');
    }, [assignedAccount]);

    // Fetch account data using useAsyncData
    const { data: riaAccountsToAssign }: any = useAsyncData(
        async () => {
            const { items } = await getAccounts({ page: 1, pageSize: 100, filter: 'UserId=null,ParentId=null' });
            return items;
        },
        [], // Empty dependencies array so it runs once
        [],
    );

    const { data: clientAccountsToAssign }: any = useAsyncData(
        async () => {
            const { items } = await getAccounts({ page: 1, pageSize: 100, filter: 'UserId=null' });
            return items;
        },
        [], // Empty dependencies array so it runs once
        [],
    );

    const { data: SubAccountsToAssign }: any = useAsyncData(
        async () => {
            const { items } = await getAccounts({
                page: 1,
                pageSize: 100,
                filter: 'ParentId=null,AccountType!=RIA',
            });
            return items;
        },
        [], // Empty dependencies array so it runs once
        [],
    );

    // Handle changes in privileges
    const handlePrivilegeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setPrivileges((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (errorMessage !== '') {
            return;
        }

        if (!role || !name || !surname || !email || !password) {
            setErrorMessage('Please fill all the required fields.');
            return;
        }

        // Only validate assignedAccount if role is not Trade Desk
        if (role !== 'Trade Desk' && !assignedAccount) {
            setErrorMessage('Please fill all the required fields.');
            return;
        }

        if (assignedAccounts.some((account) => account.id === assignedAccount?.id)) {
            setErrorMessage('A client account cannot be the same as the parent account.');
            return;
        }

        setIsSubmitting(true); // Start submitting

        try {
            const body = {
                username: email,
                email: email,
                firstName: name,
                lastName: surname,
                credentials: [
                    { type: 'password', value: password }, // Use the password entered by the user
                ],
                accounts: assignedAccounts, // Send full account objects
                role: role === 'Advisor' ? 'RIA' : role === 'Trade Desk' ? 'TradeDesk' : 'Client',
                advisorAccountId: assignedAccount?.id,
                CanLogin: privileges.canLogin,
                CanSeePositions: privileges.canSeePosition,
                CanSee1099: privileges.canSee1099,
            };

            // Send the user data to the API
            const response = await addUsers(body);
            if (response === 'Successfully Created User') {
                setSnackbarMessage('User created successfully!');
                setIsDrawerOpen(true);
                if (selectedAccount) {
                    setClientRefresh(true);
                }
                onCancel(); // Close the form
            } else {
                setErrorMessage('Error creating user: ' + response);
            }
        } catch (error) {
            // Handle unexpected errors
            setErrorMessage('Error creating user: Please try again.');
            setSnackbarMessage('Error creating user. Please try again.');
        } finally {
            setIsSubmitting(false); // Stop submitting
        }
    };

    const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    const validatePassword = (password: any) => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters long.';
        }
        if (!/[A-Z]/.test(password)) {
            return 'Password must include at least 1 uppercase letter.';
        }
        if (!/[a-z]/.test(password)) {
            return 'Password must include at least 1 lowercase letter.';
        }
        if (!/\d/.test(password)) {
            return 'Password must include at least 1 digit.';
        }
        if (!/[@$!%*?&]/.test(password)) {
            return 'Password must include at least 1 special character.';
        }
        return ''; // No error
    };

    const handleBlur = (password: any) => {
        const validationError = validatePassword(password);
        setErrorMessage(validationError); // Set the specific error message
    };

    const isFormComplete = () => {
        // For Trade Desk role, only validate name, surname, email, and password
        if (role === 'Trade Desk') {
            return name && surname && email && passwordValidationRegex.test(password);
        }
        // For other roles, validate all fields including assignedAccount
        return name && surname && email && passwordValidationRegex.test(password) && role && assignedAccount;
    };

    return (
        <form onSubmit={handleSubmit}>
            {errorMessage && <div className='red-div-create-user'>{errorMessage}</div>}
            {!selectedAccount && (
                <FormControl
                    component='fieldset'
                    fullWidth
                    margin='normal'
                    required
                >
                    <FormLabel component='legend'>Please select the role</FormLabel>
                    <RadioGroup
                        row
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                    >
                        <FormControlLabel
                            value='Advisor'
                            control={<Radio />}
                            label='Advisor'
                        />
                        <FormControlLabel
                            value='Sub Account'
                            control={<Radio />}
                            label='Sub Account'
                        />
                        <FormControlLabel
                            value='Trade Desk'
                            control={<Radio />}
                            label='Trade Desk'
                        />
                    </RadioGroup>
                </FormControl>
            )}
            <div className='create-user-form'>
                <TextField
                    label='Name'
                    variant='outlined'
                    fullWidth
                    margin='normal'
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (isFormComplete()) setErrorMessage('');
                    }}
                    required
                />

                <TextField
                    label='Last Name'
                    variant='outlined'
                    fullWidth
                    margin='normal'
                    value={surname}
                    onChange={(e) => {
                        setSurname(e.target.value);
                        if (isFormComplete()) setErrorMessage('');
                    }}
                    required
                />
            </div>
            <TextField
                label='Email'
                variant='outlined'
                fullWidth
                margin='normal'
                type='email'
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    if (isFormComplete()) setErrorMessage('');
                }}
                required
            />

            {/* Password Field */}
            <TextField
                label='Password'
                variant='outlined'
                fullWidth
                margin='normal'
                type='password'
                value={password}
                onChange={(e) => {
                    const value = e.target.value;
                    setPassword(value);
                    handleBlur(value);
                }}
                required
                onBlur={() => {
                    handleBlur(password);
                }}
            />

            {/* Only show account selection if role is not Trade Desk */}
            {role !== 'Trade Desk' && (
                <>
                    {!selectedAccount && (
                        <FormControl
                            fullWidth
                            margin='normal'
                        >
                            <InputLabel required>
                                {role === 'Advisor' ? 'Link RIA Account' : 'Assign Accounts'}
                            </InputLabel>
                            <Select
                                required
                                value={assignedAccount || ''}
                                onChange={(e) => {
                                    const selectedAccount = e.target.value === '' ? null : e.target.value;
                                    setAssignedAccount(selectedAccount);
                                    if (isFormComplete()) setErrorMessage('');
                                }}
                                input={
                                    <OutlinedInput
                                        label={role === 'Advisor' ? 'Link RIA Account' : 'Assign Accounts'}
                                    />
                                }
                                renderValue={(selected) =>
                                    selected ? (
                                        <Chip
                                            label={
                                                selected.verificationRequest?.firstName +
                                                ' ' +
                                                selected.verificationRequest?.lastName +
                                                ' {' +
                                                selected.fdid +
                                                '}'
                                            }
                                        />
                                    ) : (
                                        'None'
                                    )
                                }
                            >
                                <MenuItem value=''>None</MenuItem>
                                {riaAccountsToAssign &&
                                    role === 'Advisor' &&
                                    riaAccountsToAssign.map((account: any) => (
                                        <MenuItem
                                            key={account.id}
                                            value={account}
                                        >
                                            {account.verificationRequest?.firstName +
                                                ' ' +
                                                account.verificationRequest?.lastName +
                                                ' {' +
                                                account.fdid +
                                                '}'}
                                        </MenuItem>
                                    ))}
                                {clientAccountsToAssign &&
                                    role === 'Sub Account' &&
                                    clientAccountsToAssign.map((account: any) => (
                                        <MenuItem
                                            key={account.id}
                                            value={account}
                                        >
                                            {account.verificationRequest?.firstName +
                                                ' ' +
                                                account.verificationRequest?.lastName +
                                                ' {' +
                                                account.fdid +
                                                '}'}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                    {role === 'Advisor' && !selectedAccount && (
                        <FormControl
                            fullWidth
                            margin='normal'
                        >
                            <InputLabel>Assign Client Accounts</InputLabel>
                            <Select
                                multiple
                                value={assignedAccounts}
                                onChange={(e) => {
                                    const selected = e.target.value;
                                    const selectedAccounts = Array.isArray(selected) ? selected : [selected];
                                    setAssignedAccounts(selectedAccounts);
                                }}
                                input={<OutlinedInput label='Assign Client Accounts' />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((account: any) => (
                                            <Chip
                                                key={account.id}
                                                label={
                                                    account.verificationRequest?.firstName +
                                                    ' ' +
                                                    account.verificationRequest?.lastName +
                                                    ' {' +
                                                    account.fdid +
                                                    '}'
                                                }
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {SubAccountsToAssign &&
                                    SubAccountsToAssign.map((account: any) => (
                                        <MenuItem
                                            key={account.id}
                                            value={account}
                                        >
                                            {account.verificationRequest?.firstName +
                                                ' ' +
                                                account.verificationRequest?.lastName +
                                                ' {' +
                                                account.fdid +
                                                '}'}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </>
            )}

            {/* Only show privileges if role is not Trade Desk */}
            {role !== 'Trade Desk' && (
                <Box mt={2}>
                    <FormLabel component='legend'>Privileges</FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={privileges.canLogin}
                                onChange={handlePrivilegeChange}
                                name='canLogin'
                            />
                        }
                        label='Login'
                    />
                    <br />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={privileges.canSeePosition}
                                onChange={handlePrivilegeChange}
                                name='canSeePosition'
                            />
                        }
                        label='See Position'
                    />
                    <br />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={privileges.canSee1099}
                                onChange={handlePrivilegeChange}
                                name='canSee1099'
                            />
                        }
                        label='See 1099'
                    />
                    <br />
                </Box>
            )}

            <Box
                display='flex'
                justifyContent='space-between'
                mt={2}
            >
                <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={isSubmitting}
                    className='submit-button-create-user'
                >
                    {isSubmitting ? <CircularProgress size={24} /> : 'Add'}
                </Button>
                <Button
                    variant='outlined'
                    color='secondary'
                    onClick={onCancel}
                    className='cancel-button-create-user'
                >
                    Cancel
                </Button>
            </Box>
        </form>
    );
};

export default CreateUserForm;
